import React, { FC, useCallback, useContext, useMemo } from 'react'
import { useField } from 'formik'
import { useTranslations } from 'next-intl'
import { PHONE_FIELD_NAME, PHONE_FIELD_DESCRIPTION, PHONE_FIELD_CONFIRMED } from '../constants'
import { getPhoneNumber } from '../../../utils/common'
import { IConfirmedPhone } from '../FormPhoneField/interfaces/FormPhoneFieldInterfaces'
import IFormValues, { Validate } from '../../../validator/IFormValues'
import { FormFieldTypes } from '../../../emun/RegistrationFormFieldTypes'
import { Languages } from '../../../interfaces/common'
import { LanguageContext } from '../../LanguageContext/LanguageContext'
import { PhoneField } from '../../PhoneField/PhoneField'
import { TextWrapper } from '../../RegistrationForm/components/TextWrapper/TextWrapper'
import { sendGATagEvent } from '../../../utils/sendGATagEvent'

type FormPhoneFieldTemplateProps<IInitialValues = IFormValues> = {
  eventGroup?: string
  previousSendCodePhone?: string
  appidQueryParam?: string
  shortForm?: boolean
  doNotShowBusy?: boolean
  withCode?: boolean
  isCallCode: boolean
  onlySmsCode?: boolean
  phoneSubmitted?: boolean
  withoutDescription?: boolean
  immediatelyShowCodeInput?: boolean
  initialValues?: IInitialValues
  codeFormError?: string
  formQueryParam?: string
  codeType: FormFieldTypes
  isShowCodeRegFormInput?: boolean
  phoneIsConfirmed?: IConfirmedPhone
  sendCodeBlur?(phone: string, smsCode: string, locale: Languages): void
  isSubmitButtonDisabledChange?(value: boolean): void
  codeFormErrorChange?(error: string): void
  sendCode(phone: string): Promise<void>
  previousSendCodePhoneChange?(phone: string): void
  isCallCodeChange?(value: boolean): void
  validate?: Validate
  setIsPhoneConfirmed?(error: boolean): void
  isShowCodeRegFormInputChange?(value: boolean): void
  setPhoneIsConfirmed?(value: IConfirmedPhone): void
}

export const FormPhoneFieldTemplate: FC<FormPhoneFieldTemplateProps> = ({
  eventGroup,
  appidQueryParam,
  withoutDescription,
  shortForm,
  previousSendCodePhone,
  initialValues,
  phoneSubmitted = false,
  immediatelyShowCodeInput = false,
  withCode = false,
  onlySmsCode = false,
  doNotShowBusy = false,
  codeFormError,
  formQueryParam,
  codeType,
  isCallCode,
  isShowCodeRegFormInput,
  phoneIsConfirmed,
  sendCodeBlur,
  codeFormErrorChange,
  isSubmitButtonDisabledChange,
  previousSendCodePhoneChange,
  sendCode,
  isCallCodeChange,
  validate,
  setIsPhoneConfirmed,
  isShowCodeRegFormInputChange,
  setPhoneIsConfirmed,
}) => {
  const t = useTranslations()
  const { language } = useContext(LanguageContext)
  const phoneFieldName = t(PHONE_FIELD_NAME)
  const [field, , helper] = useField('phone')
  const confirmedPhone = phoneIsConfirmed?.phone === field.value
  const phoneFieldDescription =
    confirmedPhone && phoneIsConfirmed?.isConfirmed ? t(PHONE_FIELD_CONFIRMED) : t(PHONE_FIELD_DESCRIPTION)

  const isPhoneSubmitted = useMemo(
    () => phoneSubmitted && initialValues?.phone === getPhoneNumber(field.value),
    [initialValues?.phone, field.value, phoneSubmitted],
  )

  const sendCodeBlurWrapper = useCallback(
    (smsCode: string, locale: Languages) => {
      if (sendCodeBlur) {
        sendCodeBlur(field.value, smsCode, locale)
      }
    },
    [sendCodeBlur, field.value],
  )

  const onBlur = useCallback(() => {
    if (shortForm) {
      sendGATagEvent('event', 'PhoneBusy', {
        event_category: eventGroup,
      })
    }

    helper.setError(undefined)
  }, [helper, shortForm, eventGroup])

  return (
    <>
      <PhoneField
        name="phone"
        label={
          withoutDescription ? (
            phoneFieldName
          ) : (
            <TextWrapper label={phoneFieldName} description={phoneFieldDescription} />
          )
        }
        onBlur={onBlur}
        shortForm={shortForm}
        eventGroup={eventGroup}
        appidQueryParam={appidQueryParam}
        language={language}
        validator={validate}
        sendCode={sendCode}
        withCode={withCode}
        codeType={codeType}
        onlySmsCode={onlySmsCode}
        doNotShowBusy={doNotShowBusy}
        immediatelyShowCodeInput={immediatelyShowCodeInput}
        isPhoneSubmitted={isPhoneSubmitted}
        previousSendCodePhone={previousSendCodePhone}
        codeFormError={codeFormError}
        isCallCode={isCallCode}
        initialValues={initialValues}
        formQueryParam={formQueryParam}
        isShowCodeRegFormInput={isShowCodeRegFormInput}
        codeFormErrorChange={codeFormErrorChange}
        isSubmitButtonDisabledChange={isSubmitButtonDisabledChange}
        previousSendCodePhoneChange={previousSendCodePhoneChange}
        isCallCodeChange={isCallCodeChange}
        sendCodeBlurWrapper={sendCodeBlurWrapper}
        setIsPhoneConfirmed={setIsPhoneConfirmed}
        isShowCodeRegFormInputChange={isShowCodeRegFormInputChange}
        setPhoneIsConfirmed={setPhoneIsConfirmed}
      />
    </>
  )
}

FormPhoneFieldTemplate.displayName = 'FormPhoneFieldTemplate'
